/**
 * Google Analytics 4 Integration
 * 
 * Provides GA4 tracking functionality with cookie consent support.
 * Analytics are only loaded after user consent is obtained.
 */

// ============================================================================
// Types
// ============================================================================

interface GtagEvent {
  event_category?: string;
  event_label?: string;
  value?: number;
  [key: string]: string | number | boolean | undefined;
}

type GtagFunction = (
  command: 'config' | 'event' | 'js' | 'consent',
  targetId: string | Date,
  config?: Record<string, unknown>
) => void;

declare global {
  interface Window {
    gtag?: GtagFunction;
    dataLayer?: Array<Record<string, unknown>>;
  }
}

// ============================================================================
// Constants
// ============================================================================

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
const CONSENT_STORAGE_KEY = 'analytics-consent';
const CONSENT_TIMESTAMP_KEY = 'analytics-consent-time';

// ============================================================================
// Consent Management
// ============================================================================

export type ConsentStatus = 'granted' | 'denied' | 'pending';

/**
 * Get the current consent status from localStorage
 */
export function getConsentStatus(): ConsentStatus {
  if (typeof window === 'undefined') return 'pending';
  
  const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
  if (stored === 'granted' || stored === 'denied') {
    return stored;
  }
  return 'pending';
}

/**
 * Check if analytics consent has been granted
 */
export function hasAnalyticsConsent(): boolean {
  return getConsentStatus() === 'granted';
}

/**
 * Set analytics consent status
 */
export function setConsentStatus(status: ConsentStatus): void {
  if (typeof window === 'undefined') return;
  
  localStorage.setItem(CONSENT_STORAGE_KEY, status);
  localStorage.setItem(CONSENT_TIMESTAMP_KEY, new Date().toISOString());
  
  // Update GA consent state
  if (window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: status === 'granted' ? 'granted' : 'denied',
    });
  }
  
  // Load GA if consent granted and not already loaded
  if (status === 'granted') {
    loadGoogleAnalytics();
  }
}

/**
 * Get the timestamp when consent was given
 */
export function getConsentTimestamp(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(CONSENT_TIMESTAMP_KEY);
}

// ============================================================================
// GA4 Loading
// ============================================================================

let isGALoaded = false;

/**
 * Load Google Analytics 4 script
 */
export function loadGoogleAnalytics(): void {
  if (typeof window === 'undefined' || isGALoaded || !GA_ID) return;
  if (!hasAnalyticsConsent()) return;
  
  // Prevent double loading
  if (document.querySelector(`script[src*="googletagmanager.com/gtag"]`)) {
    isGALoaded = true;
    return;
  }
  
  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  
  // Define gtag function
  window.gtag = function gtag(...args: Parameters<GtagFunction>) {
    window.dataLayer?.push(args as unknown as Record<string, unknown>);
  };
  
  // Set default consent to denied
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  
  // Set current timestamp
  window.gtag('js', new Date());
  
  // Configure GA
  window.gtag('config', GA_ID, {
    send_page_view: false, // We'll handle page views manually
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    cookie_flags: 'SameSite=Strict;Secure',
    cookie_expires: 63072000, // 2 years
    custom_map: {
      custom_parameter_1: 'environment',
    },
    environment: import.meta.env.VITE_ENVIRONMENT || 'development',
  });
  
  // Load the GA script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);
  
  isGALoaded = true;
  
  // Update consent to granted
  window.gtag('consent', 'update', {
    analytics_storage: 'granted',
  });
}

/**
 * Initialize analytics (call this early in app lifecycle)
 */
export function initializeAnalytics(): void {
  if (hasAnalyticsConsent()) {
    loadGoogleAnalytics();
  }
}

// ============================================================================
// Event Tracking
// ============================================================================

/**
 * Track a generic event
 */
export function trackEvent(
  category: string,
  action: string,
  label?: string,
  value?: number,
  additionalParams?: Record<string, string | number | boolean>
): void {
  if (!window.gtag || !hasAnalyticsConsent()) return;
  
  const eventData: GtagEvent = {
    event_category: category,
    ...(label && { event_label: label }),
    ...(value !== undefined && { value }),
    ...additionalParams,
  };
  
  window.gtag('event', action, eventData);
}

/**
 * Track a page view
 */
export function trackPageView(path: string, title?: string): void {
  if (!window.gtag || !hasAnalyticsConsent()) return;
  
  window.gtag('config', GA_ID!, {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href,
  });
}

/**
 * Track contact form submission
 */
export function trackContactFormSubmit(success: boolean = true): void {
  trackEvent(
    'engagement',
    'contact_form_submit',
    success ? 'success' : 'error',
    undefined,
    { success: String(success) }
  );
}

/**
 * Track event detail click/expansion
 */
export function trackEventClick(eventId: string, eventTitle: string): void {
  trackEvent(
    'engagement',
    'event_detail_expand',
    eventTitle,
    undefined,
    { event_id: eventId }
  );
}

/**
 * Track PDF download
 */
export function trackDownload(fileName: string, fileType: string = 'pdf'): void {
  trackEvent(
    'engagement',
    'file_download',
    fileName,
    undefined,
    { file_type: fileType, file_name: fileName }
  );
}

/**
 * Track navigation click
 */
export function trackNavigationClick(navItem: string, url: string): void {
  trackEvent(
    'navigation',
    'nav_click',
    navItem,
    undefined,
    { nav_url: url }
  );
}

/**
 * Track outbound link click
 */
export function trackOutboundLink(url: string, label?: string): void {
  trackEvent(
    'navigation',
    'outbound_click',
    label || url,
    undefined,
    { outbound_url: url }
  );
}

/**
 * Track search/query on site
 */
export function trackSearch(searchTerm: string, resultsCount?: number): void {
  trackEvent(
    'engagement',
    'search',
    searchTerm,
    resultsCount,
    { search_term: searchTerm }
  );
}

// ============================================================================
// Utility
// ============================================================================

/**
 * Check if analytics is currently enabled
 */
export function isAnalyticsEnabled(): boolean {
  return isGALoaded && hasAnalyticsConsent() && !!window.gtag;
}

/**
 * Get analytics debug info
 */
export function getAnalyticsInfo(): {
  gaId: string | undefined;
  isLoaded: boolean;
  consentStatus: ConsentStatus;
  consentTimestamp: string | null;
} {
  return {
    gaId: GA_ID,
    isLoaded: isGALoaded,
    consentStatus: getConsentStatus(),
    consentTimestamp: getConsentTimestamp(),
  };
}
